import MediaQuery from 'react-responsive';
import { useCallback, useState } from 'react';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera/constants/breakpoints';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { getMenuGaAttributes } from 'tradera/components/site-header/site-header.analytics';

import { useQualtive } from 'tradera/hooks/use-qualtive';
import { SURVEYS } from 'tradera/constants/surveys';
import { closeProfileMenu } from 'tradera/state/ui/reducer';
import { MenuItem } from 'tradera/components/site-header/dropdown-menu-item';
import { ExpandableMenuItem } from 'tradera/components/site-header/dropdown-menu-item-expandable';
import { LocalizationMenuItem } from './expandable-localization-menu-item';
import { CurrencyMenuItem } from './expandable-currency-menu-item';
import {
    InvoiceUnpaidAlert,
    InvoiceOverdueAlert,
    PayoutFailedAlert,
    PayoutReadyBankAccountMissingAlert,
    HyperwalletPayeeVerificationAlert,
    HyperwalletPayeeVerificationReviewAlert,
    KycQuestionnaireAlert,
    PayoutReadyVerificationRequiredAlert,
    OutstandingTraderaPayLicenseAlert,
    PayoutReadyOutstandingTraderaPayLicenseAlert,
    CollectVatNumberAlert,
    CollectTinAlert,
    PayoutProviderOnboardingFailedAlert,
    AutomaticCombinedShippingTip,
} from './profile-menu-alerts';
import { ProfileHeader } from './profile-header';
import AuctionIcon from '@tradera/blueprint/theme/icons2/auction.svg?jsx';
import BookmarkIcon from '@tradera/blueprint/theme/icons2/bookmark.svg?jsx';
import BuyIcon from '@tradera/blueprint/theme/icons2/buy.svg?jsx';
import ChatIcon from '@tradera/blueprint/theme/icons2/chat.svg?jsx';
import MessageIcon from '@tradera/blueprint/theme/icons2/message.svg?jsx';
import FeedbackIcon from 'tradera/blueprint/theme/icons2/feedback.svg?jsx';
import DocumentIcon from '@tradera/blueprint/theme/icons2/document.svg?jsx';
import HeartIcon from '@tradera/blueprint/theme/icons2/heart.svg?jsx';
import OverviewIcon from '@tradera/blueprint/theme/icons2/overview.svg?jsx';
import ListIcon from '@tradera/blueprint/theme/icons2/list.svg?jsx';
import LogoutIcon from '@tradera/blueprint/theme/icons2/logout.svg?jsx';
import ShippingBagIcon from '@tradera/blueprint/theme/icons2/shipping-bag.svg?jsx';
import PenIcon from '@tradera/blueprint/theme/icons2/pen.svg?jsx';
import RatingPositiveIcon from '@tradera/blueprint/theme/icons2/rating-positive.svg?jsx';
import SaleIcon from '@tradera/blueprint/theme/icons2/sale.svg?jsx';
import SettingsIcon from '@tradera/blueprint/theme/icons2/settings.svg?jsx';
import SoldIcon from '@tradera/blueprint/theme/icons2/sold.svg?jsx';
import ListingsIcon from '@tradera/blueprint/theme/icons2/listings.svg?jsx';
import StarIcon from '@tradera/blueprint/theme/icons2/star.svg?jsx';
import UnsoldIcon from '@tradera/blueprint/theme/icons2/unsold.svg?jsx';
import UserIcon from '@tradera/blueprint/theme/icons2/user.svg?jsx';
import WalletIcon from '@tradera/blueprint/theme/icons2/wallet.svg?jsx';
import WinningIcon from '@tradera/blueprint/theme/icons2/winning.svg?jsx';
import TemplateIcon from '@tradera/blueprint/theme/icons2/templates.svg?jsx';
import GiftCardIcon from '@tradera/blueprint/theme/icons2/gift-card.svg?jsx';
import { logOutAction } from 'tradera/state/ui/actions';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { REDIRECT_TO_NEWDRAFT_QUERY } from 'tradera/apps/syi/constants';
import { selectIsUnderage } from 'tradera/state/member/selectors';
import { UnderageModal } from 'tradera/components/underage/underage-modal';
import { useGetMessagingStatsQuery } from 'tradera/state/services/messaging-stats-api';
import { Divider } from 'tradera/components/divider/divider';
import { SafeAreaSpacer } from 'tradera/components/safe-area-spacer/safe-area-spacer';
import { FashionToggleMenuItem } from './fashion-toggle-menu-item';
import { useCookieUtil } from 'tradera/hooks/use-cookie-util';
import { ENTERED_FASHION_PAGE } from 'tradera/constants/cookies';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { myTraderaUrls } from 'tradera/apps/my-tradera/app/pages/routes';

const mediaBelowLarge = { maxWidth: BREAKPOINT_SIZES[BREAKPOINTS.MD].max };

const getGaAttributes = (label: string) =>
    getMenuGaAttributes('Top navigation', 'Account menu', label);

const trackOnClick = (label: string) => {
    GtmService.trackAction('Top navigation', 'Account menu', `Link - ${label}`);
};

const trackExpandItemClick = (label: string) => {
    GtmService.trackAction('Top navigation', 'Account menu', label);
};

type ProfileMenuContentProps = {
    onClose?: () => void;
};

export const ProfileMenuContent = ({ onClose }: ProfileMenuContentProps) => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();
    const cookieUtil = useCookieUtil();
    const {
        cleanedMemberAlias,
        memberId,
        hasTraderaPay,
        memberCountryCodeIso2,
    } = useAppSelector((state) => state.member);
    const isUnderage = useAppSelector(selectIsUnderage);
    const showMessagingLink = useIsFeatureEnabled('messaging');
    const hasEnteredFashionPage =
        cookieUtil.readCookie(ENTERED_FASHION_PAGE) === 'true';

    const { data: { threadsUnread } = {} } = useGetMessagingStatsQuery(
        undefined,
        { skip: !showMessagingLink },
    );

    const [showUnderageModal, setShowUnderageModal] = useState(false);

    const { systemMessages, systemBadges } = useAppSelector(
        (state) => state.ui,
    );
    const { toLocalizedUrl } = useUrlLocalizer();
    const isTraderaShopEnabled = useIsFeatureEnabled('tradera-shop');
    const { isQualtiveEnabled, openQualtiveModal } = useQualtive();

    const isFashionToggleEnabled = useIsFeatureEnabled('fashion-toggle');
    const isMobileBottomNavEnabled = useIsFeatureEnabled('mobile-bottom-nav');
    const showGiftCardLink =
        useIsFeatureEnabled('gift-card-in-profile-menu') &&
        isSwedenCountryCodeOrUndefined(memberCountryCodeIso2);

    const closeProfileMenuCallback = useCallback(
        () => dispatch(closeProfileMenu()),
        [dispatch],
    );

    return (
        <div className="w-100 h-100 d-flex flex-column">
            <ProfileHeader onClose={onClose} />
            <nav className="d-flex flex-column overflow-auto h-100">
                <UnderageModal
                    show={showUnderageModal}
                    onClose={() => setShowUnderageModal(false)}
                />
                <ul className="plain-list">
                    {!isUnderage && (
                        <MediaQuery {...mediaBelowLarge}>
                            <MenuItem
                                label={t('siteHeader_createItem')}
                                link={toLocalizedUrl(
                                    `/selling?${REDIRECT_TO_NEWDRAFT_QUERY}`,
                                )}
                                icon={<PenIcon className="icon-md" />}
                                onClick={closeProfileMenuCallback}
                                border
                                highlight
                                gaAttributes={getGaAttributes(
                                    'Create new listing - from top',
                                )}
                            />
                        </MediaQuery>
                    )}
                    {showGiftCardLink && (
                        <MenuItem
                            label={t('siteHeader_buyGiftCard')}
                            link={toLocalizedUrl('/giftcards')}
                            icon={<GiftCardIcon className="icon-md" />}
                            onClick={closeProfileMenuCallback}
                            border
                            gaAttributes={getGaAttributes('Buy gift card')}
                        />
                    )}
                    <MenuItem
                        label={t('siteHeader_overview')}
                        link={toLocalizedUrl('/my/overview')}
                        icon={<OverviewIcon className="icon-md" />}
                        onClick={closeProfileMenuCallback}
                        border
                        gaAttributes={getGaAttributes('My Tradera Overview')}
                    />
                    {showMessagingLink && (
                        <MenuItem
                            label={t('siteHeader_messages')}
                            link={toLocalizedUrl('/messaging')}
                            icon={<MessageIcon className="icon-md" />}
                            onClick={closeProfileMenuCallback}
                            border
                            notify={threadsUnread}
                            follow={false}
                            gaAttributes={getGaAttributes('Messages')}
                        />
                    )}
                    {!isMobileBottomNavEnabled && (
                        <MediaQuery {...mediaBelowLarge}>
                            <Divider thick className="my-0" />
                            <ExpandableMenuItem
                                label={t('siteHeader_watchlist')}
                                icon={<HeartIcon className="icon-md" />}
                                indentItems
                                border>
                                <MenuItem
                                    label={t('siteHeader_wishlist')}
                                    link={toLocalizedUrl('/my/wish-list')}
                                    icon={<HeartIcon className="icon-md" />}
                                    onClick={closeProfileMenuCallback}
                                    border
                                    gaAttributes={getGaAttributes('Wishlist')}
                                />
                                <MenuItem
                                    label={t('siteHeader_savedSearches')}
                                    link={toLocalizedUrl('/my/saved-searches')}
                                    icon={<BookmarkIcon className="icon-md" />}
                                    onClick={closeProfileMenuCallback}
                                    border
                                    gaAttributes={getGaAttributes(
                                        'Saved searches',
                                    )}
                                />
                                <MenuItem
                                    label={t('siteHeader_sellerList')}
                                    link={toLocalizedUrl('/my/seller-list')}
                                    onClick={closeProfileMenuCallback}
                                    icon={<StarIcon className="icon-md" />}
                                    gaAttributes={getGaAttributes(
                                        'Seller list',
                                    )}
                                />
                            </ExpandableMenuItem>
                            <ExpandableMenuItem
                                label={t('siteHeader_buying')}
                                icon={<BuyIcon className="icon-md" />}
                                indentItems
                                border>
                                <MenuItem
                                    label={t('siteHeader_ongoingAuctions')}
                                    link={toLocalizedUrl(myTraderaUrls.bidding)}
                                    icon={<AuctionIcon className="icon-md" />}
                                    onClick={closeProfileMenuCallback}
                                    border
                                    gaAttributes={getGaAttributes(
                                        'Ongoing auctions',
                                    )}
                                />
                                <MenuItem
                                    label={t('siteHeader_boughtItems')}
                                    link={toLocalizedUrl('/my/purchases')}
                                    icon={<WinningIcon className="icon-md" />}
                                    onClick={closeProfileMenuCallback}
                                    border
                                    gaAttributes={getGaAttributes(
                                        'Won / Bought',
                                    )}
                                />
                                <MenuItem
                                    label={t('siteHeader_rateSeller')}
                                    link={toLocalizedUrl(
                                        '/my/leave-feedback/buyer',
                                    )}
                                    icon={
                                        <RatingPositiveIcon className="icon-md" />
                                    }
                                    onClick={closeProfileMenuCallback}
                                    gaAttributes={getGaAttributes(
                                        'Rate seller',
                                    )}
                                />
                            </ExpandableMenuItem>
                            {isUnderage ? (
                                <MenuItem
                                    label={t('siteHeader_selling_underage')}
                                    border
                                    icon={<SaleIcon className="icon-md" />}
                                    onClick={() => setShowUnderageModal(true)}
                                />
                            ) : (
                                <ExpandableMenuItem
                                    label={t('siteHeader_selling')}
                                    icon={<SaleIcon className="icon-md" />}
                                    indentItems
                                    border>
                                    <MenuItem
                                        label={t('siteHeader_activeItems')}
                                        link={toLocalizedUrl(
                                            myTraderaUrls.listings,
                                        )}
                                        icon={
                                            <ListingsIcon className="icon-md" />
                                        }
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Active listings',
                                        )}
                                    />
                                    <MenuItem
                                        label={t('siteHeader_soldItems')}
                                        link={toLocalizedUrl('/my/sold')}
                                        icon={<SoldIcon className="icon-md" />}
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Sold listings',
                                        )}
                                    />
                                    <MenuItem
                                        label={t('siteHeader_unsoldItems')}
                                        link={toLocalizedUrl(
                                            myTraderaUrls.listingsUnsold,
                                        )}
                                        icon={
                                            <UnsoldIcon className="icon-md" />
                                        }
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Unsold listings',
                                        )}
                                    />

                                    <MenuItem
                                        label={t('siteHeader_picklist')}
                                        link={toLocalizedUrl('/my/picklist')}
                                        icon={<ListIcon className="icon-md" />}
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Picklist',
                                        )}
                                    />
                                    <MenuItem
                                        label={t('siteHeader_createItem')}
                                        link={toLocalizedUrl(
                                            `/selling?${REDIRECT_TO_NEWDRAFT_QUERY}`,
                                        )}
                                        icon={<PenIcon className="icon-md" />}
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Create new listing',
                                        )}
                                    />
                                    <MenuItem
                                        label={t('siteHeader_templates')}
                                        link={toLocalizedUrl(
                                            '/selling/templates',
                                        )}
                                        icon={
                                            <TemplateIcon className="icon-md" />
                                        }
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Listing templates',
                                        )}
                                    />
                                    <MenuItem
                                        label={t('siteHeader_rateBuyer')}
                                        link={toLocalizedUrl(
                                            '/my/leave-feedback/seller',
                                        )}
                                        icon={
                                            <RatingPositiveIcon className="icon-md" />
                                        }
                                        onClick={closeProfileMenuCallback}
                                        border
                                        gaAttributes={getGaAttributes(
                                            'Rate buyer',
                                        )}
                                    />
                                    {isTraderaShopEnabled && (
                                        <MenuItem
                                            label={t(
                                                'siteHeader_buyShippingSupplies',
                                            )}
                                            link={toLocalizedUrl(
                                                '/tradera-shop',
                                            )}
                                            icon={
                                                <ShippingBagIcon className="icon-md" />
                                            }
                                            onClick={closeProfileMenuCallback}
                                            gaAttributes={getGaAttributes(
                                                'To Tradera Shop',
                                            )}
                                        />
                                    )}
                                </ExpandableMenuItem>
                            )}
                        </MediaQuery>
                    )}
                    <MenuItem
                        label={t('siteHeader_myProfile')}
                        link={toLocalizedUrl(
                            `/profile/items/${memberId}/${cleanedMemberAlias}`,
                        )}
                        icon={<UserIcon className="icon-md" />}
                        inSPA={true}
                        onClick={closeProfileMenuCallback}
                        border
                        gaAttributes={getGaAttributes('My profile')}
                    />
                    <MenuItem
                        label={t('siteHeader_myRatings')}
                        link={toLocalizedUrl(
                            `/profile/feedback/${memberId}/${cleanedMemberAlias}`,
                        )}
                        icon={<RatingPositiveIcon className="icon-md" />}
                        inSPA={true}
                        onClick={closeProfileMenuCallback}
                        border
                        gaAttributes={getGaAttributes('Ratings')}
                    />
                    <Divider thick className="my-0" />
                    {hasTraderaPay && (
                        <MenuItem
                            label={t('siteheader_paymentsAndFees')}
                            link={toLocalizedUrl('/my/payouts')}
                            icon={<WalletIcon className="icon-md" />}
                            onClick={closeProfileMenuCallback}
                            alerts={[
                                systemMessages.PayoutReadyBankAccountMissing && (
                                    <PayoutReadyBankAccountMissingAlert
                                        key={'PayoutReadyBankAccountMissing'}
                                        severity={
                                            systemMessages
                                                .PayoutReadyBankAccountMissing
                                                .severity
                                        }
                                    />
                                ),
                                systemMessages.PayoutFailed && (
                                    <PayoutFailedAlert
                                        key={'PayoutFailed'}
                                        severity={
                                            systemMessages.PayoutFailed.severity
                                        }
                                    />
                                ),
                                systemMessages.HyperwalletPayeeVerificationRequired && (
                                    <HyperwalletPayeeVerificationAlert
                                        key="HyperwalletPayeeVerification"
                                        severity={
                                            systemMessages
                                                .HyperwalletPayeeVerificationRequired
                                                .severity
                                        }
                                    />
                                ),
                                systemMessages.HyperwalletPayeeVerificationUnderReview && (
                                    <HyperwalletPayeeVerificationReviewAlert
                                        key="HyperwalletPayeeVerificationReview"
                                        severity={
                                            systemMessages
                                                .HyperwalletPayeeVerificationUnderReview
                                                .severity
                                        }
                                    />
                                ),
                                systemMessages.PayoutReadyVerificationRequired && (
                                    <PayoutReadyVerificationRequiredAlert
                                        key={'PayoutReadyVerificationRequired'}
                                        severity={
                                            systemMessages
                                                .PayoutReadyVerificationRequired
                                                .severity
                                        }
                                    />
                                ),
                                systemMessages.OutstandingTraderaPayLicense && (
                                    <OutstandingTraderaPayLicenseAlert
                                        key={'OutstandingTraderaPayLicense'}
                                        severity={
                                            systemMessages
                                                .OutstandingTraderaPayLicense
                                                .severity
                                        }
                                    />
                                ),
                                systemMessages.PayoutReadyOutstandingTraderaPayLicense && (
                                    <PayoutReadyOutstandingTraderaPayLicenseAlert
                                        key={
                                            'PayoutReadyOutstandingTraderaPayLicense'
                                        }
                                        severity={
                                            systemMessages
                                                .PayoutReadyOutstandingTraderaPayLicense
                                                .severity
                                        }
                                    />
                                ),
                                systemMessages.PayoutProviderOnboardingFailed && (
                                    <PayoutProviderOnboardingFailedAlert
                                        key={'PayoutProviderOnboardingFailed'}
                                        severity={
                                            systemMessages
                                                .PayoutProviderOnboardingFailed
                                                .severity
                                        }
                                    />
                                ),
                            ]}
                            badge={systemBadges.WalletBalance}
                            notify={systemBadges.PayoutsActivity?.count}
                            border
                            gaAttributes={getGaAttributes('Payouts')}
                        />
                    )}
                    <MenuItem
                        label={t('siteheader_invoices')}
                        link={toLocalizedUrl('/my/invoices')}
                        icon={<DocumentIcon className="icon-md" />}
                        onClick={closeProfileMenuCallback}
                        alerts={[
                            systemMessages.InvoiceUnpaid && (
                                <InvoiceUnpaidAlert
                                    key={'InvoiceUnpaid'}
                                    severity={
                                        systemMessages.InvoiceUnpaid.severity
                                    }
                                />
                            ),
                            systemMessages.InvoiceOverdue && (
                                <InvoiceOverdueAlert
                                    key={'InvoiceOverdue'}
                                    severity={
                                        systemMessages.InvoiceOverdue.severity
                                    }
                                />
                            ),
                        ]}
                        border
                        gaAttributes={getGaAttributes('Invoices')}
                    />
                    <MenuItem
                        label={t('siteheader_settings')}
                        link={toLocalizedUrl('/my/settings')}
                        icon={<SettingsIcon className="icon-md" />}
                        onClick={closeProfileMenuCallback}
                        alerts={[
                            systemMessages.KycQuestionnaire && (
                                <KycQuestionnaireAlert
                                    key="KycQuestionnaire"
                                    severity={
                                        systemMessages.KycQuestionnaire.severity
                                    }
                                />
                            ),
                            systemMessages.CollectVatNumber && (
                                <CollectVatNumberAlert
                                    key="CollectVatNumber"
                                    severity={
                                        systemMessages.CollectVatNumber.severity
                                    }
                                />
                            ),
                            systemMessages.CollectTin && (
                                <CollectTinAlert
                                    key="CollectTin"
                                    severity={
                                        systemMessages.CollectTin.severity
                                    }
                                />
                            ),
                            systemMessages.AutomaticCombinedShippingTip && (
                                <AutomaticCombinedShippingTip
                                    key="AutomaticCombinedShippingTip"
                                    severity={
                                        systemMessages
                                            .AutomaticCombinedShippingTip
                                            .severity
                                    }
                                />
                            ),
                        ]}
                        border
                        gaAttributes={getGaAttributes('Settings')}
                    />
                    <LocalizationMenuItem onToggle={trackExpandItemClick} />
                    <CurrencyMenuItem onToggle={trackExpandItemClick} />
                    <Divider thick className="my-0" />
                    {isQualtiveEnabled && (
                        <MenuItem
                            label={t('siteheader_feedback')}
                            className="font-weight-normal"
                            border
                            icon={<FeedbackIcon className="icon-md" />}
                            onClick={() => {
                                openQualtiveModal(SURVEYS.general);
                                trackOnClick('Leave feedback Qualtive');
                            }}
                        />
                    )}
                    <MenuItem
                        label={t('siteheader_customerService')}
                        link={t('siteheader_customerService_link')}
                        icon={<ChatIcon className="icon-md" />}
                        border
                        gaAttributes={getGaAttributes('Customer service')}
                    />
                    {(isFashionToggleEnabled || hasEnteredFashionPage) && (
                        <>
                            <Divider thick className="my-0" />
                            <FashionToggleMenuItem />
                        </>
                    )}
                    <MenuItem
                        label={t('siteHeader_logOut')}
                        onClick={() => dispatch(logOutAction())}
                        link={toLocalizedUrl('/login/logout')}
                        icon={<LogoutIcon className="icon-md" />}
                        gaAttributes={getGaAttributes('Logout')}
                    />
                    <SafeAreaSpacer />
                </ul>
            </nav>
        </div>
    );
};
